/**
 * Scene Notes Field Component
 * Sprint 19+: Enhanced Scene Cards
 * 
 * Editable text area for scene guidance/notes
 * - Shows/hides based on content
 * - Max 2 lines with ellipsis when collapsed
 * - Pre-populated from templates
 * - Click to expand and edit
 */

import React, { useState, useRef, useEffect } from 'react';
import { Input } from 'antd';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';
import './SceneNotesField.scss';

const { TextArea } = Input;

interface SceneNotesFieldProps {
  value?: string;
  onChange: (newValue: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  autoExpand?: boolean; // Expand on mount if has content
}

export const SceneNotesField: React.FC<SceneNotesFieldProps> = ({
  value = '',
  onChange,
  placeholder = 'Add notes or guidance for this scene...',
  maxLength = 500,
  disabled = false,
  autoExpand = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(autoExpand && !!value);
  const [editValue, setEditValue] = useState(value);
  const textAreaRef = useRef<any>(null);

  // Update editValue when value prop changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!disabled) {
      setIsExpanded(true);
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    
    if (trimmedValue !== value) {
      onChange(trimmedValue);
    }
    
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
    // Allow Ctrl/Cmd+Enter to save
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // If no value and not editing, show add button
  if (!value && !isEditing) {
    return (
      <div 
        className="scene-notes-field empty"
        onClick={handleClick}
      >
        <FileText size={14} className="notes-icon" />
        <span className="placeholder-text">{placeholder}</span>
      </div>
    );
  }

  // Editing mode
  if (isEditing) {
    return (
      <div className="scene-notes-field editing">
        <div className="notes-header">
          <FileText size={14} className="notes-icon" />
          <span className="notes-label">Scene Notes</span>
        </div>
        <TextArea
          ref={textAreaRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={maxLength}
          autoSize={{ minRows: 2, maxRows: 6 }}
          className="notes-textarea"
        />
        <div className="notes-hint">
          Press Ctrl+Enter to save, Escape to cancel
        </div>
      </div>
    );
  }

  // Display mode - collapsed or expanded
  return (
    <div 
      className={`scene-notes-field ${isExpanded ? 'expanded' : 'collapsed'}`}
      onClick={handleClick}
    >
      <div className="notes-header">
        <FileText size={14} className="notes-icon" />
        <span className="notes-label">Notes</span>
        {value && value.length > 100 && (
          <button 
            className="expand-button"
            onClick={toggleExpanded}
          >
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
      </div>
      <div className={`notes-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {value}
      </div>
    </div>
  );
};
